import React, { useState, useEffect, useRef } from 'react';
import OpenAI from 'openai';
import {
  Box,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Stack
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SpeechToTextButton from './SpeechToTextButton';

const GhostTextCompletion = ({ label, name, value, handleChange, noEdit, className, max, none }) => {
  const [input, setInput] = useState(value || '');
  const [suggestion, setSuggestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const textareaRef = useRef(null);

  const openai = new OpenAI({
    baseURL: 'https://api.deepseek.com',
    apiKey: process.env.REACT_APP_DEEPSEEK_API_KEY,
    dangerouslyAllowBrowser: true
  });

  useEffect(() => {
    const fetchCompletion = async () => {
      if (input.length < 3) {
        setSuggestion('');
        return;
      }

      setIsLoading(true);
      try {
        const completion = await openai.chat.completions.create({
          messages: [
            {
              role: "system",
              content: "Predict the most likely continuation of this text. Assume correspondence in an organization. Return only the text that would come next, without repeating the input:"
            },
            { role: "user", content: input }
          ],
          model: "deepseek-chat",
          max_tokens: 15,
          temperature: 0.7
        });

        setSuggestion(completion.choices[0].message.content.trim());
      } catch (error) {
        console.error("Completion error:", error);
        setSuggestion('');
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchCompletion, 300);
    return () => clearTimeout(timer);
  }, [input]);

  const handleKeyDown = (e) => {
    // Accept suggestion on Tab or Right Arrow
    if ((e.key === 'Tab' || e.key === 'ArrowRight') && suggestion) {
      e.preventDefault();
      acceptSuggestion();
    }
  };

  const acceptSuggestion = () => {
    setInput(prev => prev + '' + suggestion);
    setSuggestion('');
    handleChange({ target: { name, value: input + '' + suggestion } });
  };

  const handleTranscript = (transcript) => {
    setInput(prev => prev + '' + transcript);
    handleChange({ target: { name, value: input + '' + transcript } });
  };

  return (
    <Stack spacing={2} sx={{ width: '100%', mt: 2 }}>
      <Paper 
        elevation={2}
        sx={{ 
          p: 3,
          bgcolor: '#ffffff',
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha('#17621A', 0.1)
        }}
      >
        {!none && (
          <Typography
            variant="h6"
            sx={{ 
              mb: 2,
              color: '#2D3748',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            {label}
          </Typography>
        )}
        
        <Box position="relative">
          <TextField
            disabled={noEdit}
            multiline
            fullWidth
            rows={4}
            inputRef={textareaRef}
            name={name}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              handleChange(e);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Start typing..."
            inputProps={{ maxLength: max }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: alpha('#F7FAFC', 0.8),
                '&:hover': {
                  backgroundColor: '#F7FAFC',
                },
                '& fieldset': {
                  borderColor: alpha('#17621A', 0.2),
                },
                '&:hover fieldset': {
                  borderColor: '#17621A',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#17621A',
                }
              }
            }}
          />
          
          {suggestion && (
            <Box 
              mt={1.5} 
              p={2}
              bgcolor={alpha('#17621A', 0.05)}
              borderRadius={1}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#4A5568',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                Suggestion:{' '}
                <Box
                  component="span"
                  sx={{ 
                    color: '#17621A',
                    fontStyle: 'italic',
                    fontWeight: 500
                  }}
                >
                  {suggestion}
                </Box>
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {isLoading && (
        <Box display="flex" justifyContent="center" mt={1}>
          <CircularProgress 
            size={24} 
            sx={{ 
              color: '#17621A'
            }} 
          />
        </Box>
      )}

      <Box mt={1}>
        <SpeechToTextButton onTranscript={handleTranscript} />
      </Box>
    </Stack>
  );
};

export default GhostTextCompletion;